import { useEffect, useState } from 'react';

/**
 * Google Analytics tracking component
 * Injects GA4 tracking code when enabled in site settings
 */
const GoogleAnalytics = ({ trackingId, enabled }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!enabled || !trackingId || initialized) return;

    // Check if already loaded
    if (window.gtag) {
      setInitialized(true);
      return;
    }

    // Create script elements
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingId}', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    `;

    document.head.appendChild(gtagScript);
    document.head.appendChild(inlineScript);
    
    setInitialized(true);
    console.log('Google Analytics initialized with ID:', trackingId);

    return () => {
      // Cleanup on unmount (optional)
    };
  }, [trackingId, enabled, initialized]);

  return null;
};

/**
 * Google Tag Manager component
 */
const GoogleTagManager = ({ containerId }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!containerId || initialized) return;

    // Check if already loaded
    if (window.google_tag_manager) {
      setInitialized(true);
      return;
    }

    // GTM script
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(script);

    // GTM noscript
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    setInitialized(true);
    console.log('Google Tag Manager initialized with ID:', containerId);
  }, [containerId, initialized]);

  return null;
};

/**
 * Facebook Pixel component
 */
const FacebookPixel = ({ pixelId }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!pixelId || initialized) return;

    // Check if already loaded
    if (window.fbq) {
      setInitialized(true);
      return;
    }

    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = '1';
    img.width = '1';
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    setInitialized(true);
    console.log('Facebook Pixel initialized with ID:', pixelId);
  }, [pixelId, initialized]);

  return null;
};

/**
 * Combined Analytics Provider
 * Fetches settings and initializes all tracking
 */
const AnalyticsProvider = ({ settings }) => {
  if (!settings) return null;

  return (
    <>
      {settings.ga_enabled && settings.ga_tracking_id && (
        <GoogleAnalytics 
          trackingId={settings.ga_tracking_id} 
          enabled={settings.ga_enabled} 
        />
      )}
      {settings.gtm_container_id && (
        <GoogleTagManager containerId={settings.gtm_container_id} />
      )}
      {settings.fb_pixel_id && (
        <FacebookPixel pixelId={settings.fb_pixel_id} />
      )}
    </>
  );
};

/**
 * Track custom events (for use in components)
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
  // Facebook Pixel
  if (window.fbq) {
    window.fbq('track', eventName, eventParams);
  }
};

/**
 * Track page views (call on route change)
 */
export const trackPageView = (path, title) => {
  if (window.gtag) {
    window.gtag('config', window.GA_TRACKING_ID || '', {
      page_path: path,
      page_title: title
    });
  }
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

export default AnalyticsProvider;
